import { Controller } from "@hotwired/stimulus";
import { effect } from "@preact/signals-core";
import { bounds, type Drawing, type DrawSize, type DrawStyle, type LabelSize, labelSize, styleOf, widthFor } from "../maps/drawings";
import { ANCHOR_RADIUS, HANDLE_PAD } from "../maps/transform";
import type MapBoard from "./map_board_controller";

const GAP = 12;
// Screen pixels the handles' frame and anchors reach past the drawing's bounds;
// the menu keeps clear of them whether or not they show, so it never jumps.
const HANDLES_REACH = HANDLE_PAD + ANCHOR_RADIUS;
// Tailwind's max-sm: below it the menu is a bottom sheet, placed by CSS.
const SHEET = "(max-width: 639px)";
const COARSE = "(pointer: coarse)";
// The share of a width in pixels, to a hundredth.
const SAME_WIDTH = 0.005;

// Connects to data-controller="map-drawing-menu", beside map-board on the
// master's own board root. The board opens this menu beside a selected
// drawing: a line or an area takes a colour, a width, a stroke, the handles
// and removal (a ledge also flips its side, and a tunnel has no colour); a label takes its text, colour, size and removal. Each pick is
// one write through the board, and the choices follow the drawing live.
export default class extends Controller {
	static targets = ["menu", "title", "text", "colorGroup", "color", "width", "size", "dash", "handles", "line", "label", "ledge"];
	declare readonly menuTarget: HTMLElement;
	declare readonly titleTarget: HTMLElement;
	declare readonly textTarget: HTMLInputElement;
	declare readonly colorGroupTarget: HTMLElement;
	declare readonly colorTargets: HTMLElement[];
	declare readonly widthTargets: HTMLElement[];
	declare readonly sizeTargets: HTMLElement[];
	declare readonly dashTargets: HTMLElement[];
	declare readonly handlesTarget: HTMLElement;
	declare readonly lineTargets: HTMLElement[];
	declare readonly labelTargets: HTMLElement[];
	declare readonly ledgeTarget: HTMLElement;
	drawingId?: string | null;
	dispose?: () => void;
	// The field was typed in since it was filled, so leaving it writes.
	dirty = false;

	disconnect() {
		this.close({ discard: true });
	}

	get board() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-board") as MapBoard | null;
	}

	get drawing() {
		return this.board?.state.view.peek().drawings?.[this.drawingId] as Drawing | undefined;
	}

	open(id: string) {
		const { state } = this.board;
		this.close();
		this.drawingId = id;
		this.dirty = false;
		this.element.setAttribute("data-drawing-menu", "open");
		this.menuTarget.hidden = false;
		this.dispose = effect(() => {
			const drawing = state.view.value.drawings?.[id] as Drawing | undefined;
			state.local.viewport.value;
			state.local.drafts.value.move;
			state.local.handles.value;
			if (!drawing) return queueMicrotask(() => this.drawingId === id && !this.drawing && this.board?.closeDrawingMenu());
			this.fill(drawing);
			this.place(drawing);
		});
		// A label's menu is for its text; a line's starts on its first choice. A
		// touch screen keeps the keyboard down until the field is tapped.
		if (this.drawing?.kind !== "text")
			[...this.menuTarget.querySelectorAll("button")].find((button) => !button.closest("[hidden]"))?.focus({ preventScroll: true });
		else if (!window.matchMedia(COARSE).matches) {
			this.textTarget.focus({ preventScroll: true });
			this.textTarget.select();
		}
	}

	// A text typed and not written is written first, unless Escape closes it,
	// which drops it and its preview.
	close({ discard = false } = {}) {
		if (!this.drawingId) return;
		if (discard) this.dirty = false;
		else this.commit();
		this.board?.clearDrawingPreview();
		this.dispose?.();
		this.dispose = null;
		this.drawingId = null;
		this.element.removeAttribute("data-drawing-menu");
		this.menuTarget.hidden = true;
		if (this.menuTarget.contains(document.activeElement)) this.board?.canvasTarget.focus({ preventScroll: true });
	}

	dismiss() {
		this.board?.closeDrawingMenu();
	}

	// One step per Escape, as the board takes them: a handle drag, the
	// handles, then the menu.
	escape() {
		this.board?.escape();
	}

	fill(drawing: Drawing) {
		const { grid } = this.board.state.view.peek().scene;
		const label = drawing.kind === "text";
		const titles = JSON.parse(this.titleTarget.dataset.titles ?? "{}");
		const style = label ? null : styleOf(drawing, grid);
		const title = label ? titles.text : (titles.styles[style] ?? titles[drawing.kind]);
		if (this.titleTarget.textContent !== title) this.titleTarget.textContent = title;
		for (const target of this.lineTargets) target.hidden = label;
		for (const target of this.labelTargets) target.hidden = !label;
		// A tunnel is drawn in the rock's ink and the floor's paper, whatever its colour, and only a ledge has a side to flip.
		this.colorGroupTarget.hidden = drawing.style === "tunnel";
		this.ledgeTarget.hidden = drawing.style !== "ledge";
		if (document.activeElement !== this.textTarget) this.textTarget.value = drawing.text ?? "";
		this.check(this.colorTargets, (value) => value === drawing.color);
		this.check(this.widthTargets, (value) => same(widthFor(value as DrawSize, grid), drawing.width));
		this.check(this.sizeTargets, (value) => same(labelSize(value as LabelSize, grid), drawing.width));
		this.check(this.dashTargets, (value) => value === drawing.style);
		this.press(this.handlesTarget, this.board.state.local.handles.peek() === drawing.id);
	}

	check(targets: HTMLElement[], matches: (value: string) => boolean) {
		for (const target of targets) this.press(target, matches(target.dataset.value ?? ""));
	}

	// Typing in the text field runs this on every key, and a needless write to
	// the DOM would split the field's own undo.
	press(target: HTMLElement, on: boolean) {
		if (target.getAttribute("aria-pressed") !== String(on)) target.setAttribute("aria-pressed", String(on));
	}

	pickColor({ currentTarget }: Event & { currentTarget: HTMLElement }) {
		this.write({ color: currentTarget.dataset.value });
	}

	pickWidth({ currentTarget }: Event & { currentTarget: HTMLElement }) {
		this.write({ width: widthFor(currentTarget.dataset.value as DrawSize, this.board.state.view.peek().scene.grid) });
	}

	pickSize({ currentTarget }: Event & { currentTarget: HTMLElement }) {
		this.write({ width: labelSize(currentTarget.dataset.value as LabelSize, this.board.state.view.peek().scene.grid) });
	}

	pickDash({ currentTarget }: Event & { currentTarget: HTMLElement }) {
		this.write({ style: currentTarget.dataset.value as DrawStyle });
	}

	// Writes nothing for the value the drawing already has.
	write(attributes: { color: string } | { width: number } | { style: DrawStyle }) {
		const drawing = this.drawing;
		const [key, value] = Object.entries(attributes)[0];
		if (!drawing || drawing[key] === value) return;
		this.board.updateDrawing(drawing.id, attributes);
	}

	// The board previews the text as it is typed.
	preview() {
		if (!this.drawingId) return;
		this.dirty = true;
		this.board?.previewDrawingText(this.drawingId, this.textTarget.value);
	}

	// Enter or leaving the field writes what was typed once, and an emptied
	// one removes the label, as the label editor does. A field left untouched
	// writes nothing, so it never overwrites another client's rename.
	commit() {
		const drawing = this.drawing;
		if (!drawing || !this.dirty) return;
		this.dirty = false;
		const text = this.textTarget.value.replace(/\s+/g, " ").trim();
		this.board.clearDrawingPreview();
		if (!text) this.board.removeDrawing(drawing.id);
		else if (text !== drawing.text) this.board.updateDrawing(drawing.id, { text });
	}

	flip() {
		if (this.drawingId) this.board.flipDrawing(this.drawingId);
	}

	toggleHandles() {
		this.board.toggleHandles();
	}

	remove() {
		if (this.drawingId) this.board.removeDrawing(this.drawingId);
	}

	// Beside the drawing's box and the room its handles take, flipped to its
	// left when the room the panels leave runs out, and clamped clear of them.
	place(drawing: Drawing) {
		const { renderer, state } = this.board;
		if (!renderer || window.matchMedia(SHEET).matches) return;
		const move = state.local.drafts.peek().move;
		const shown = move?.id === drawing.id ? { ...drawing, points: drawing.points.map((value, index) => value + (index % 2 ? move.dy : move.dx)) } : drawing;
		const box = bounds(shown, renderer.labelWidth, this.board.state.view.peek().scene.grid);
		const from = renderer.imageToClient({ x: box.x, y: box.y + box.height / 2 });
		const to = renderer.imageToClient({ x: box.x + box.width, y: box.y + box.height / 2 });
		if (!from || !to) return;
		const frame = (this.menuTarget.offsetParent ?? this.element).getBoundingClientRect();
		const menu = this.menuTarget.getBoundingClientRect();
		const insets = this.board.chromeInsets();
		const [least, most] = [insets.left + GAP, frame.width - insets.right - GAP - menu.width];
		let left = to.x - frame.left + HANDLES_REACH + GAP;
		if (left > most) left = from.x - frame.left - HANDLES_REACH - GAP - menu.width;
		left = Math.min(Math.max(left, least), Math.max(most, least));
		const [highest, lowest] = [insets.top + GAP, frame.height - insets.bottom - GAP - menu.height];
		const top = Math.min(Math.max(to.y - frame.top - menu.height / 2, highest), Math.max(lowest, highest));
		this.menuTarget.style.left = `${left}px`;
		this.menuTarget.style.top = `${top}px`;
	}
}

function same(a: number, b: number) {
	return Math.abs(a - b) < SAME_WIDTH;
}
